package app.alefly.analytics

import app.alefly.domain.model.ChallengeId
import app.alefly.domain.model.Difficulty
import app.alefly.domain.model.Platform
import app.alefly.domain.model.QuestionId
import app.alefly.domain.model.QuizId
import app.alefly.domain.model.TenantId

interface QuizEventFactory {
    fun quizStart(
        tenantId: TenantId,
        quizId: QuizId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        questionCount: Int,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.QUIZ_START,
        context = AnalyticsContext(
            tenantId = tenantId,
            quizId = quizId,
            difficulty = difficulty,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "questionCount" to questionCount.toString(),
        ),
    )

    fun quizComplete(
        tenantId: TenantId,
        quizId: QuizId,
        challengeId: ChallengeId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        score: Int,
        total: Int,
        durationMs: Long,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.QUIZ_COMPLETE,
        context = quizContext(tenantId, quizId, challengeId, difficulty, locale, platform),
        attributes = mapOf(
            "score" to score.toString(),
            "total" to total.toString(),
            "durationMs" to durationMs.toString(),
        ),
    )

    fun quizAnswer(
        tenantId: TenantId,
        quizId: QuizId,
        challengeId: ChallengeId,
        questionId: QuestionId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        selectedOptionId: String,
        isCorrect: Boolean,
        usedHint: Boolean,
        answeredCount: Int,
        timeSpentMs: Long? = null,
        questionCount: Int? = null,
        optionOrder: String? = null,
        selectedOptionPosition: Int? = null,
        correctOptionPosition: Int? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.QUIZ_ANSWER,
        context = quizContext(tenantId, quizId, challengeId, difficulty, locale, platform),
        attributes = quizAnswerAttributes(
            questionId, selectedOptionId, isCorrect, usedHint, answeredCount, timeSpentMs,
            questionCount, optionOrder, selectedOptionPosition, correctOptionPosition,
        ),
    )

    fun quizAbandon(
        tenantId: TenantId,
        quizId: QuizId,
        challengeId: ChallengeId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        answeredCount: Int,
        totalQuestions: Int,
        questionId: String? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.QUIZ_ABANDON,
        context = quizContext(tenantId, quizId, challengeId, difficulty, locale, platform),
        attributes = buildMap {
            put("quizAnsweredCount", answeredCount.toString())
            put("totalQuestions", totalQuestions.toString())
            questionId?.let { put("questionId", it) }
        },
    )

    fun questionView(
        tenantId: TenantId,
        challengeId: ChallengeId,
        questionId: QuestionId,
        questionIndex: Int,
        isFirst: Boolean,
        isLast: Boolean,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        questionCount: Int? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.QUESTION_VIEW,
        context = challengeContext(tenantId, challengeId, difficulty, locale, platform),
        attributes = buildMap {
            put("questionId", questionId.value)
            put("questionIndex", questionIndex.toString())
            put("isFirst", if (isFirst) "1" else "0")
            put("isLast", if (isLast) "1" else "0")
            questionCount?.let { put("questionCount", it.toString()) }
        },
    )

    fun hintUsed(
        tenantId: TenantId,
        challengeId: ChallengeId,
        questionId: QuestionId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        viaAd: Boolean = false,
        hintType: String = HintType.TEXT,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.HINT_USED,
        context = challengeContext(tenantId, challengeId, difficulty, locale, platform),
        attributes = mapOf(
            "questionId" to questionId.value,
            "via_ad" to viaAd.toString(),
            "hintType" to hintType,
        ),
    )

    fun hintRequested(
        tenantId: TenantId,
        challengeId: ChallengeId,
        questionId: QuestionId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.HINT_REQUESTED,
        context = challengeContext(tenantId, challengeId, difficulty, locale, platform),
        attributes = mapOf(
            "questionId" to questionId.value,
        ),
    )
}

private fun quizAnswerAttributes(
    questionId: QuestionId,
    selectedOptionId: String,
    isCorrect: Boolean,
    usedHint: Boolean,
    answeredCount: Int,
    timeSpentMs: Long?,
    questionCount: Int?,
    optionOrder: String?,
    selectedOptionPosition: Int?,
    correctOptionPosition: Int?,
): Map<String, String> = buildMap {
    put("questionId", questionId.value)
    put("selectedOptionId", selectedOptionId)
    put("isCorrect", if (isCorrect) "1" else "0")
    put("usedHint", if (usedHint) "1" else "0")
    put("quizAnsweredCount", answeredCount.toString())
    timeSpentMs?.let { put("timeSpentMs", it.toString()) }
    questionCount?.let { put("questionCount", it.toString()) }
    optionOrder?.let { put("optionOrder", it) }
    selectedOptionPosition?.let { put("selectedOptionPosition", it.toString()) }
    correctOptionPosition?.let { put("correctOptionPosition", it.toString()) }
}

private fun quizContext(
    tenantId: TenantId,
    quizId: QuizId,
    challengeId: ChallengeId,
    difficulty: Difficulty,
    locale: String,
    platform: Platform,
): AnalyticsContext = AnalyticsContext(
    tenantId = tenantId,
    quizId = quizId,
    challengeId = challengeId,
    difficulty = difficulty,
    locale = locale,
    platform = platform,
)

private fun challengeContext(
    tenantId: TenantId,
    challengeId: ChallengeId,
    difficulty: Difficulty,
    locale: String,
    platform: Platform,
): AnalyticsContext = AnalyticsContext(
    tenantId = tenantId,
    challengeId = challengeId,
    difficulty = difficulty,
    locale = locale,
    platform = platform,
)
